// ============================================================================
// CHANGO V5.0 — Ops CMS · Per-page editor (Task #51 Phase 1 + Task #52 Phase 2)
// ============================================================================
// Route: /ops/cms/pages/:slug   — admin only (gated in main.jsx)
//
// Capabilities shipped:
//   · Edit page metadata (title, meta_description, meta_keywords, og_image_url)
//     via PATCH /api/ops/site/pages/:slug
//   · Manage the `current_revision` block list (add / remove / reorder /
//     enable-disable / edit inline) for ALL eight block kinds, then save with
//     PUT /api/ops/site/pages/:slug/blocks  (atomic replace of the revision)
//   · Publish the current draft via
//     POST /api/ops/site/pages/:slug/publish
//   · Rollback to a specific prior revision via
//     POST /api/ops/site/pages/:slug/rollback  { target_revision, reason }
//
// Block kinds with full inline editors:
//   Phase 1 (Task #51): hero, pricing
//   Phase 2 (Task #52): features, testimonials, cta, rich_text, media, faq
//
// The ReadonlyBlock fallback is retained ONLY as defence against a row with an
// unrecognised `kind` (e.g. DB was hand-edited) so it still round-trips.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api, HttpError } from '../../../api/client.js';
import { useAuth } from '../../../hooks/useAuth.js';
import {
  normalizeBlock,
  isSafeHref,
  LIMITS,
  BLOCK_KINDS,
  MEDIA_TYPES,
} from './cmsHelpers.js';

// ---- small UI helpers ------------------------------------------------------

function Banner({ kind, children }) {
  if (!children) return null;
  const tone = {
    error: 'border-rose-500/40 bg-rose-900/30 text-rose-200',
    ok:    'border-emerald-500/40 bg-emerald-900/30 text-emerald-200',
    warn:  'border-amber-500/40 bg-amber-900/30 text-amber-200',
    info:  'border-slate-500/40 bg-slate-800/60 text-slate-200',
  }[kind || 'info'];
  return (
    <div className={`mb-4 rounded-md border px-4 py-2 text-sm ${tone}`}>{children}</div>
  );
}

function LabeledInput({ label, value, onChange, max, placeholder, hint, required }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-200">
        {label}{required && <span className="ml-1 text-rose-400">*</span>}
      </span>
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        maxLength={max}
        placeholder={placeholder || ''}
        className="mt-1 w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:border-chango-500 focus:outline-none"
      />
      {hint && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
    </label>
  );
}

function LabeledArea({ label, value, onChange, max, rows = 3, hint }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-200">{label}</span>
      <textarea
        rows={rows}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        maxLength={max}
        className="mt-1 w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:border-chango-500 focus:outline-none"
      />
      {hint && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
    </label>
  );
}

function CtaFields({ label, value, onChange }) {
  const v = value || { label: '', href: '', target: '_self' };
  const href = v.href ?? '';
  const hrefBad = href !== '' && !isSafeHref(href);
  return (
    <fieldset className="mt-3 rounded border border-slate-700 bg-slate-900/40 p-3">
      <legend className="px-2 text-xs text-slate-400">{label}</legend>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <LabeledInput
          label="按钮文案"
          value={v.label}
          onChange={(x) => onChange({ ...v, label: x })}
          max={LIMITS.CTA_LABEL}
          placeholder="例如 立即体验"
        />
        <LabeledInput
          label="链接"
          value={v.href}
          onChange={(x) => onChange({ ...v, href: x })}
          max={LIMITS.URL}
          placeholder="/register 或 https://…"
          hint={hrefBad ? '仅允许 http(s) 或以 / 开头的相对路径' : ''}
        />
        <label className="block">
          <span className="text-sm font-medium text-slate-200">打开方式</span>
          <select
            value={v.target === '_blank' ? '_blank' : '_self'}
            onChange={(e) => onChange({ ...v, target: e.target.value })}
            className="mt-1 w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:border-chango-500 focus:outline-none"
          >
            <option value="_self">当前标签</option>
            <option value="_blank">新标签</option>
          </select>
        </label>
      </div>
      <p className="mt-2 text-xs text-slate-500">两个字段都留空 = 不展示此按钮。</p>
    </fieldset>
  );
}

// ---- block editors ---------------------------------------------------------

function HeroEditor({ block, onPatch }) {
  const p = block.payload || {};
  const patch = (k, v) => onPatch({ ...p, [k]: v });
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <LabeledInput
        label="主标题"
        required
        value={p.title}
        onChange={(v) => patch('title', v)}
        max={LIMITS.SHORT_TEXT}
        placeholder="例如 让出口报价不再靠记忆"
      />
      <LabeledInput
        label="背景图 URL"
        value={p.bg_image_url}
        onChange={(v) => patch('bg_image_url', v)}
        max={LIMITS.URL}
        placeholder="https://…"
      />
      <LabeledArea
        label="副标题"
        value={p.subtitle}
        onChange={(v) => patch('subtitle', v)}
        max={LIMITS.MED_TEXT}
        rows={2}
      />
      <div className="md:col-span-2">
        <CtaFields
          label="主按钮"
          value={p.primary_cta}
          onChange={(v) => patch('primary_cta', v)}
        />
        <CtaFields
          label="次按钮"
          value={p.secondary_cta}
          onChange={(v) => patch('secondary_cta', v)}
        />
      </div>
    </div>
  );
}

function PricingEditor({ block, onPatch }) {
  const plans = Array.isArray(block.payload?.plans) ? block.payload.plans : [];
  const setPlans = (next) => onPatch({ ...block.payload, plans: next });

  const patchPlan = (idx, delta) => {
    const next = plans.map((x, i) => i === idx ? { ...x, ...delta } : x);
    setPlans(next);
  };

  const addPlan = () => {
    if (plans.length >= LIMITS.MAX_PLANS) return;
    setPlans([...plans, {
      sku_code: '', name: '', tagline: '', highlights: [],
      cta: { label: '', href: '', target: '_self' }, featured: false,
    }]);
  };

  const removePlan = (idx) => setPlans(plans.filter((_, i) => i !== idx));
  const movePlan = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= plans.length) return;
    const next = plans.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    setPlans(next);
  };

  return (
    <div>
      {plans.length === 0 && (
        <p className="rounded border border-slate-700 bg-slate-900/40 p-3 text-xs text-slate-400">
          暂无定价方案。至少需要 1 个。
        </p>
      )}
      <div className="space-y-3">
        {plans.map((pl, i) => {
          const hlText = Array.isArray(pl.highlights) ? pl.highlights.join('\n') : '';
          return (
            <div key={i} className="rounded border border-slate-700 bg-slate-900/40 p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400">方案 #{i + 1}</span>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => movePlan(i, -1)} disabled={i === 0}
                    className="rounded border border-slate-600 px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-40">上移</button>
                  <button type="button" onClick={() => movePlan(i, 1)} disabled={i === plans.length - 1}
                    className="rounded border border-slate-600 px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-40">下移</button>
                  <button type="button" onClick={() => removePlan(i)}
                    className="rounded bg-rose-700/60 px-2 py-0.5 text-xs text-white hover:bg-rose-700">删除</button>
                </div>
              </div>
              <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
                <LabeledInput
                  label="SKU 代码"
                  required
                  value={pl.sku_code}
                  onChange={(v) => patchPlan(i, { sku_code: v })}
                  max={LIMITS.SKU_CODE}
                  placeholder="例如 STARTER_MONTHLY"
                  hint="价格不在本处配置 — 实际金额读取自 sku_pricing (§20 #19)"
                />
                <LabeledInput
                  label="方案名称"
                  required
                  value={pl.name}
                  onChange={(v) => patchPlan(i, { name: v })}
                  max={LIMITS.SHORT_TEXT}
                  placeholder="例如 入门版"
                />
                <LabeledArea
                  label="副标题 / 卖点一句话"
                  value={pl.tagline}
                  onChange={(v) => patchPlan(i, { tagline: v })}
                  max={LIMITS.MED_TEXT}
                  rows={2}
                />
                <LabeledArea
                  label={`要点列表 (每行 1 条 · ≤ ${LIMITS.MAX_HIGHLIGHTS} 条)`}
                  value={hlText}
                  onChange={(v) => {
                    const arr = v.split('\n').map((s) => s.trim()).filter(Boolean);
                    patchPlan(i, { highlights: arr });
                  }}
                  max={LIMITS.MAX_HIGHLIGHTS * (LIMITS.SHORT_TEXT + 2)}
                  rows={4}
                />
              </div>
              <div className="mt-2">
                <CtaFields
                  label="卡片按钮"
                  value={pl.cta}
                  onChange={(v) => patchPlan(i, { cta: v })}
                />
              </div>
              <label className="mt-2 flex items-center gap-2 text-sm text-slate-200">
                <input
                  type="checkbox"
                  checked={Boolean(pl.featured)}
                  onChange={(e) => patchPlan(i, { featured: e.target.checked })}
                />
                标记为推荐方案
              </label>
            </div>
          );
        })}
      </div>
      <button
        type="button"
        onClick={addPlan}
        disabled={plans.length >= LIMITS.MAX_PLANS}
        className="mt-3 rounded border border-dashed border-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-50"
      >
        + 新增方案（上限 {LIMITS.MAX_PLANS}）
      </button>
    </div>
  );
}

// ---- Phase-2 block editors (Task #52) --------------------------------------

// A small helper: render a "repeatable list" toolbar (up / down / delete) so
// we don't repeat the same 15-line JSX in every Phase-2 editor.
function ListRowToolbar({ idx, total, onMove, onRemove, label }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-400">{label ?? `#${idx + 1}`}</span>
      <div className="flex items-center gap-1">
        <button type="button" onClick={() => onMove(-1)} disabled={idx === 0}
          className="rounded border border-slate-600 px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-40">上移</button>
        <button type="button" onClick={() => onMove(1)} disabled={idx === total - 1}
          className="rounded border border-slate-600 px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-40">下移</button>
        <button type="button" onClick={onRemove}
          className="rounded bg-rose-700/60 px-2 py-0.5 text-xs text-white hover:bg-rose-700">删除</button>
      </div>
    </div>
  );
}

function FeaturesEditor({ block, onPatch }) {
  const p = block.payload || {};
  const items = Array.isArray(p.items) ? p.items : [];
  const setItems = (next) => onPatch({ ...p, items: next });
  const patchItem = (idx, delta) => setItems(items.map((x, i) => i === idx ? { ...x, ...delta } : x));
  const add = () => {
    if (items.length >= LIMITS.MAX_FEATURES) return;
    setItems([...items, { label: '', description: '', icon: '' }]);
  };
  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
  };
  return (
    <div className="space-y-3">
      <LabeledInput
        label="模块标题 (可选)"
        value={p.heading}
        onChange={(v) => onPatch({ ...p, heading: v })}
        max={LIMITS.SHORT_TEXT}
        placeholder="例如 为什么选择 CHANGO"
      />
      {items.length === 0 && (
        <p className="rounded border border-slate-700 bg-slate-900/40 p-3 text-xs text-slate-400">
          至少需要 1 个特性。
        </p>
      )}
      {items.map((it, i) => (
        <div key={i} className="rounded border border-slate-700 bg-slate-900/40 p-3">
          <ListRowToolbar idx={i} total={items.length}
            onMove={(d) => move(i, d)} onRemove={() => remove(i)}
            label={`特性 #${i + 1}`} />
          <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
            <LabeledInput label="标题" required
              value={it.label} onChange={(v) => patchItem(i, { label: v })}
              max={LIMITS.SHORT_TEXT} placeholder="例如 一键生成报价单" />
            <LabeledInput label="图标 (icon 名称或 emoji)"
              value={it.icon} onChange={(v) => patchItem(i, { icon: v })}
              max={LIMITS.ICON} placeholder="例如 ⚡ 或 bolt" />
            <div className="md:col-span-2">
              <LabeledArea label="描述 (可选)"
                value={it.description}
                onChange={(v) => patchItem(i, { description: v })}
                max={LIMITS.MED_TEXT} rows={2} />
            </div>
          </div>
        </div>
      ))}
      <button type="button" onClick={add}
        disabled={items.length >= LIMITS.MAX_FEATURES}
        className="rounded border border-dashed border-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-50">
        + 新增特性（上限 {LIMITS.MAX_FEATURES}）
      </button>
    </div>
  );
}

function TestimonialsEditor({ block, onPatch }) {
  const p = block.payload || {};
  const items = Array.isArray(p.items) ? p.items : [];
  const setItems = (next) => onPatch({ ...p, items: next });
  const patchItem = (idx, delta) => setItems(items.map((x, i) => i === idx ? { ...x, ...delta } : x));
  const add = () => {
    if (items.length >= LIMITS.MAX_TESTIMONIALS) return;
    setItems([...items, { quote: '', name: '', title: '', avatar_url: '' }]);
  };
  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
  };
  return (
    <div className="space-y-3">
      {items.length === 0 && (
        <p className="rounded border border-slate-700 bg-slate-900/40 p-3 text-xs text-slate-400">
          至少需要 1 条客户证言。
        </p>
      )}
      {items.map((t, i) => (
        <div key={i} className="rounded border border-slate-700 bg-slate-900/40 p-3">
          <ListRowToolbar idx={i} total={items.length}
            onMove={(d) => move(i, d)} onRemove={() => remove(i)}
            label={`证言 #${i + 1}`} />
          <div className="mt-2 space-y-2">
            <LabeledArea label="引语" max={LIMITS.LONG_TEXT} rows={3}
              value={t.quote}
              onChange={(v) => patchItem(i, { quote: v })} />
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <LabeledInput label="姓名" required
                value={t.name} onChange={(v) => patchItem(i, { name: v })}
                max={LIMITS.SHORT_TEXT} placeholder="例如 张伟" />
              <LabeledInput label="职位 / 公司"
                value={t.title} onChange={(v) => patchItem(i, { title: v })}
                max={LIMITS.SHORT_TEXT} placeholder="例如 外贸经理 · 某某进出口" />
              <LabeledInput label="头像 URL (可选)"
                value={t.avatar_url}
                onChange={(v) => patchItem(i, { avatar_url: v })}
                max={LIMITS.URL} placeholder="https://…" />
            </div>
          </div>
        </div>
      ))}
      <button type="button" onClick={add}
        disabled={items.length >= LIMITS.MAX_TESTIMONIALS}
        className="rounded border border-dashed border-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-50">
        + 新增证言（上限 {LIMITS.MAX_TESTIMONIALS}）
      </button>
    </div>
  );
}

function CtaBlockEditor({ block, onPatch }) {
  const p = block.payload || {};
  const patch = (k, v) => onPatch({ ...p, [k]: v });
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <LabeledInput label="标题" required
        value={p.title} onChange={(v) => patch('title', v)}
        max={LIMITS.SHORT_TEXT} placeholder="例如 开始你的 30 天免费试用" />
      <LabeledArea label="副标题"
        value={p.subtitle} onChange={(v) => patch('subtitle', v)}
        max={LIMITS.MED_TEXT} rows={2} />
      <div className="md:col-span-2">
        <CtaFields label="主按钮" value={p.primary_cta}
          onChange={(v) => patch('primary_cta', v)} />
        <p className="mt-1 text-xs text-amber-300">
          CTA 块至少需要 1 个按钮；未填会在保存/发布时报错。
        </p>
      </div>
    </div>
  );
}

function RichTextEditor({ block, onPatch }) {
  const p = block.payload || {};
  return (
    <div>
      <LabeledArea label={`Markdown 正文 (上限 ${LIMITS.RICH_TEXT_BODY} 字符)`}
        value={p.body_md} onChange={(v) => onPatch({ ...p, body_md: v })}
        max={LIMITS.RICH_TEXT_BODY} rows={10}
        hint="支持标题 (# ## ###)、粗体、斜体、列表、链接和代码块。前端渲染时做 XSS 防护。" />
    </div>
  );
}

function MediaEditor({ block, onPatch }) {
  const p = block.payload || {};
  const patch = (k, v) => onPatch({ ...p, [k]: v });
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <label className="block">
        <span className="text-sm font-medium text-slate-200">类型<span className="ml-1 text-rose-400">*</span></span>
        <select
          value={MEDIA_TYPES.includes(p.media_type) ? p.media_type : ''}
          onChange={(e) => patch('media_type', e.target.value)}
          className="mt-1 w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:border-chango-500 focus:outline-none"
        >
          <option value="">— 请选择 —</option>
          <option value="image">图片</option>
          <option value="video">视频 (mp4 / webm URL)</option>
          <option value="embed">嵌入 (iframe 兼容 URL)</option>
        </select>
      </label>
      <LabeledInput label="资源 URL" required
        value={p.url} onChange={(v) => patch('url', v)}
        max={LIMITS.URL} placeholder="https://… 或以 / 开头的路径" />
      <LabeledArea label="图注 / 说明"
        value={p.caption} onChange={(v) => patch('caption', v)}
        max={LIMITS.MED_TEXT} rows={2} />
      <LabeledArea label="Alt 文本 (无障碍描述)"
        value={p.alt_text} onChange={(v) => patch('alt_text', v)}
        max={LIMITS.MED_TEXT} rows={2}
        hint="图片专用；视频/嵌入可留空。屏幕阅读器会读取此文本。" />
    </div>
  );
}

function FaqEditor({ block, onPatch }) {
  const p = block.payload || {};
  const items = Array.isArray(p.items) ? p.items : [];
  const setItems = (next) => onPatch({ ...p, items: next });
  const patchItem = (idx, delta) => setItems(items.map((x, i) => i === idx ? { ...x, ...delta } : x));
  const add = () => {
    if (items.length >= LIMITS.MAX_FAQ) return;
    setItems([...items, { question: '', answer_md: '' }]);
  };
  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
  };
  return (
    <div className="space-y-3">
      {items.length === 0 && (
        <p className="rounded border border-slate-700 bg-slate-900/40 p-3 text-xs text-slate-400">
          至少需要 1 条 FAQ。
        </p>
      )}
      {items.map((f, i) => (
        <div key={i} className="rounded border border-slate-700 bg-slate-900/40 p-3">
          <ListRowToolbar idx={i} total={items.length}
            onMove={(d) => move(i, d)} onRemove={() => remove(i)}
            label={`Q${i + 1}`} />
          <div className="mt-2 space-y-2">
            <LabeledInput label="问题" required
              value={f.question}
              onChange={(v) => patchItem(i, { question: v })}
              max={LIMITS.SHORT_TEXT}
              placeholder="例如 支持哪些支付方式？" />
            <LabeledArea label="答案 (Markdown)"
              value={f.answer_md}
              onChange={(v) => patchItem(i, { answer_md: v })}
              max={LIMITS.LONG_TEXT} rows={4} />
          </div>
        </div>
      ))}
      <button type="button" onClick={add}
        disabled={items.length >= LIMITS.MAX_FAQ}
        className="rounded border border-dashed border-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-50">
        + 新增 FAQ（上限 {LIMITS.MAX_FAQ}）
      </button>
    </div>
  );
}

// Read-only preview — retained only for rows with an unrecognised `kind`
// (e.g. DB hand-edits to a future kind we haven't shipped yet).
function ReadonlyBlock({ block }) {
  return (
    <pre className="overflow-auto rounded bg-slate-950/60 p-3 text-xs text-slate-300">
      {JSON.stringify(block.payload ?? {}, null, 2)}
    </pre>
  );
}

// ---- page-level data loader ------------------------------------------------

function usePageDetail(slug) {
  const [state, setState] = useState({
    page: null,
    revision: null,
    blocks: [],
    loading: true,
    error: null,
  });
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((x) => x + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      try {
        const res = await api.get(`/api/ops/site/pages/${encodeURIComponent(slug)}`);
        if (cancelled) return;
        const blocks = Array.isArray(res?.blocks) ? res.blocks : [];
        // Parse payload if the server returned a stringified JSON (mysql2
        // connection options may differ between envs).
        const parsed = blocks.map((b) => ({
          ...b,
          payload: typeof b.payload === 'string' ? safeParse(b.payload) : (b.payload ?? {}),
        }));
        setState({
          page: res?.page || null,
          revision: res?.revision ?? null,
          blocks: parsed,
          loading: false,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        setState((s) => ({ ...s, loading: false, error: err.message || '加载失败' }));
      }
    })();
    return () => { cancelled = true; };
  }, [slug, reloadKey]);

  return { ...state, reload };
}

function safeParse(s) {
  try { return JSON.parse(s); } catch { return {}; }
}

// ---- main component --------------------------------------------------------

// Every block kind now has an inline editor.
const EDITABLE_KINDS = new Set(BLOCK_KINDS);

// A friendly zh-CN label so the "add block" menu reads naturally.
const KIND_LABEL = {
  hero:         'Hero 主图',
  features:     '特性列表',
  pricing:      '定价方案',
  testimonials: '客户证言',
  cta:          '行动号召 (CTA)',
  rich_text:    '富文本 (Markdown)',
  media:        '媒体 (图/视频/嵌入)',
  faq:          '常见问题',
};

export default function OpsCmsPageEditor() {
  const { user } = useAuth();
  const { slug } = useParams();
  const navigate = useNavigate();
  const { page, revision, blocks: loadedBlocks, loading, error, reload } = usePageDetail(slug);

  // ---- local state: metadata form + draft blocks ---------------------------
  const [meta, setMeta] = useState({
    title: '', meta_description: '', meta_keywords: '', og_image_url: '',
  });
  const [blocks, setBlocks] = useState([]);
  const [savingMeta, setSavingMeta] = useState(false);
  const [savingBlocks, setSavingBlocks] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [rollback, setRollback] = useState({ open: false, target: '', reason: '' });
  const [banner, setBanner] = useState(null); // { kind, msg }

  // Sync server → local once each load completes.
  useEffect(() => {
    if (page) {
      setMeta({
        title: page.title ?? '',
        meta_description: page.meta_description ?? '',
        meta_keywords: page.meta_keywords ?? '',
        og_image_url: page.og_image_url ?? '',
      });
    }
    setBlocks(loadedBlocks);
  }, [page, loadedBlocks]);

  const hasDraftBlocks = useMemo(() => blocks.length > 0, [blocks]);
  const allEditableValid = useMemo(() => {
    for (const b of blocks) {
      if (!EDITABLE_KINDS.has(b.kind)) continue; // unknown kinds pass through.
      const r = normalizeBlock(b.kind, b.payload);
      if (!r.ok) {
        const label = KIND_LABEL[b.kind] || b.kind;
        return { ok: false, where: `${label}: ${r.field} — ${r.message}` };
      }
    }
    return { ok: true };
  }, [blocks]);

  // ---- block mutations -----------------------------------------------------

  const addBlock = (kind) => {
    const baseByKind = {
      hero:         { title: '', subtitle: '', bg_image_url: '',
                      primary_cta: null, secondary_cta: null },
      pricing:      { plans: [] },
      features:     { heading: '', items: [] },
      testimonials: { items: [] },
      cta:          { title: '', subtitle: '',
                      primary_cta: { label: '', href: '', target: '_self' } },
      rich_text:    { body_md: '' },
      media:        { media_type: '', url: '', caption: '', alt_text: '' },
      faq:          { items: [] },
    };
    setBlocks((cur) => [
      ...cur,
      {
        id: `new-${Date.now()}-${cur.length}`,
        kind,
        order_idx: cur.length,
        enabled: 1,
        payload: baseByKind[kind] ?? {},
      },
    ]);
  };

  const patchBlock = (idx, nextPayload) => {
    setBlocks((cur) => cur.map((b, i) => i === idx ? { ...b, payload: nextPayload } : b));
  };
  const removeBlock = (idx) => setBlocks((cur) => cur.filter((_, i) => i !== idx));
  const moveBlock = (idx, dir) => {
    const j = idx + dir;
    setBlocks((cur) => {
      if (j < 0 || j >= cur.length) return cur;
      const next = cur.slice();
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };
  const toggleBlock = (idx) => {
    setBlocks((cur) => cur.map((b, i) => i === idx ? { ...b, enabled: b.enabled ? 0 : 1 } : b));
  };

  // ---- network actions -----------------------------------------------------

  const saveMeta = async () => {
    setSavingMeta(true);
    setBanner(null);
    try {
      const body = {
        title: meta.title,
        meta_description: meta.meta_description || null,
        meta_keywords: meta.meta_keywords || null,
        og_image_url: meta.og_image_url || null,
      };
      await api.patch(`/api/ops/site/pages/${encodeURIComponent(slug)}`, body);
      setBanner({ kind: 'ok', msg: '页面信息已保存' });
      reload();
    } catch (err) {
      setBanner({ kind: 'error', msg: err.message || '保存失败' });
    } finally {
      setSavingMeta(false);
    }
  };

  const saveBlocks = async () => {
    if (!allEditableValid.ok) {
      setBanner({ kind: 'error', msg: `存在未填/过长项：${allEditableValid.where}` });
      return;
    }
    setSavingBlocks(true);
    setBanner(null);
    try {
      const payload = {
        blocks: blocks.map((b, i) => ({
          kind: b.kind,
          order_idx: i,
          enabled: b.enabled !== 0,
          payload: b.payload ?? {},
        })),
      };
      await api.put(`/api/ops/site/pages/${encodeURIComponent(slug)}/blocks`, payload);
      setBanner({ kind: 'ok', msg: `已保存 ${payload.blocks.length} 个块到草稿 r${revision}` });
      reload();
    } catch (err) {
      setBanner({ kind: 'error', msg: err.message || '保存失败' });
    } finally {
      setSavingBlocks(false);
    }
  };

  const publishNow = async () => {
    if (!hasDraftBlocks) {
      setBanner({ kind: 'error', msg: '当前草稿没有块；先新增至少 1 个块。' });
      return;
    }
    if (!allEditableValid.ok) {
      setBanner({ kind: 'error', msg: `存在未填/过长项：${allEditableValid.where}` });
      return;
    }
    setPublishing(true);
    setBanner(null);
    try {
      // Persist latest edits first — publish snapshots what's in the DB, not
      // the in-memory state.
      const save = {
        blocks: blocks.map((b, i) => ({
          kind: b.kind,
          order_idx: i,
          enabled: b.enabled !== 0,
          payload: b.payload ?? {},
        })),
      };
      await api.put(`/api/ops/site/pages/${encodeURIComponent(slug)}/blocks`, save);
      const res = await api.post(`/api/ops/site/pages/${encodeURIComponent(slug)}/publish`, {});
      setBanner({ kind: 'ok', msg: `已发布 · 线上现在是 r${res?.published_revision}` });
      reload();
    } catch (err) {
      setBanner({ kind: 'error', msg: err.message || '发布失败' });
    } finally {
      setPublishing(false);
    }
  };

  const doRollback = async () => {
    const target = Number.parseInt(rollback.target, 10);
    if (!Number.isInteger(target) || target <= 0) {
      setBanner({ kind: 'error', msg: '回滚的 revision 必须是正整数' });
      return;
    }
    if (rollback.reason.trim().length < 8) {
      setBanner({ kind: 'error', msg: '回滚需要 ≥ 8 字符的理由' });
      return;
    }
    try {
      await api.post(`/api/ops/site/pages/${encodeURIComponent(slug)}/rollback`, {
        target_revision: target,
        reason: rollback.reason.trim(),
      });
      setBanner({ kind: 'ok', msg: `已回滚 · 线上现在是 r${target}` });
      setRollback({ open: false, target: '', reason: '' });
      reload();
    } catch (err) {
      setBanner({ kind: 'error', msg: err.message || '回滚失败' });
    }
  };

  // ---- render --------------------------------------------------------------

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 px-6 py-10 text-center text-slate-300">
        加载中…
      </div>
    );
  }
  if (error || !page) {
    return (
      <div className="min-h-screen bg-slate-900 px-6 py-10">
        <Banner kind="error">{error || '页面不存在'}</Banner>
        <Link to="/ops/cms/pages" className="text-chango-300 underline">返回页面列表</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      <header className="border-b border-slate-700 bg-slate-800/80">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="text-lg font-semibold">
              CMS · 编辑页面 <code className="rounded bg-slate-700 px-2 py-0.5 text-sm">{slug}</code>
            </h1>
            <p className="text-xs text-slate-400">
              路由 {page.route} · 状态 {page.status} ·
              {' 草稿 r'}{page.current_revision}
              {page.published_revision > 0 && <> · 线上 r{page.published_revision}</>}
              {' · '}
              <Link to="/ops/cms/pages" className="underline">页面列表</Link>
            </p>
          </div>
          <div className="text-right text-xs text-slate-400">
            {user?.email}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-8">
        {banner && <Banner kind={banner.kind}>{banner.msg}</Banner>}

        {/* ---- Metadata panel ------------------------------------------- */}
        <section className="mb-8 rounded-lg border border-slate-700 bg-slate-800/40 p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-200">页面元信息 (SEO)</h2>
            <button
              type="button"
              onClick={saveMeta}
              disabled={savingMeta}
              className="rounded bg-chango-600 px-3 py-1 text-xs font-semibold text-white hover:bg-chango-700 disabled:opacity-50"
            >
              {savingMeta ? '保存中…' : '保存元信息'}
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <LabeledInput label="页面标题" required
              value={meta.title} onChange={(v) => setMeta((m) => ({ ...m, title: v }))} max={200} />
            <LabeledInput label="Open Graph 分享图 URL"
              value={meta.og_image_url} onChange={(v) => setMeta((m) => ({ ...m, og_image_url: v }))} max={LIMITS.URL} />
            <LabeledArea label="Meta Description"
              value={meta.meta_description}
              onChange={(v) => setMeta((m) => ({ ...m, meta_description: v }))}
              max={LIMITS.MED_TEXT} rows={2} />
            <LabeledArea label="Meta Keywords"
              value={meta.meta_keywords}
              onChange={(v) => setMeta((m) => ({ ...m, meta_keywords: v }))}
              max={LIMITS.MED_TEXT} rows={2}
              hint="逗号分隔，面向搜索引擎。近年来权重较低，但保留字段以便兼容。" />
          </div>
        </section>

        {/* ---- Blocks panel ---------------------------------------------- */}
        <section className="mb-8 rounded-lg border border-slate-700 bg-slate-800/40 p-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-200">内容块 · 草稿 r{revision}</h2>
              <p className="text-xs text-slate-400">
                保存仅写入草稿；点击“发布”后线上才会看到新内容。
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-2 rounded border border-slate-600 bg-slate-800 px-2 py-1 text-xs text-slate-200">
                <span>+ 新增块</span>
                <select
                  value=""
                  onChange={(e) => { if (e.target.value) { addBlock(e.target.value); e.target.value = ''; } }}
                  className="rounded bg-slate-800 text-slate-100 focus:outline-none"
                >
                  <option value="">— 选择类型 —</option>
                  {BLOCK_KINDS.map((k) => (
                    <option key={k} value={k}>{KIND_LABEL[k] || k}</option>
                  ))}
                </select>
              </label>
              <button type="button" onClick={saveBlocks} disabled={savingBlocks}
                className="rounded bg-slate-700 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-600 disabled:opacity-50">
                {savingBlocks ? '保存中…' : '保存草稿'}
              </button>
              <button type="button" onClick={publishNow} disabled={publishing}
                className="rounded bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                {publishing ? '发布中…' : '发布到线上'}
              </button>
            </div>
          </div>

          {blocks.length === 0 && (
            <p className="rounded border border-slate-700 bg-slate-900/40 p-4 text-sm text-slate-400">
              草稿无内容块。从右上角“新增块”下拉选择类型开始编辑
              (Hero / Features / Pricing / Testimonials / CTA / Rich Text / Media / FAQ)。
            </p>
          )}

          <div className="space-y-4">
            {blocks.map((b, i) => {
              const editable = EDITABLE_KINDS.has(b.kind);
              return (
                <div key={b.id ?? i} className="rounded-lg border border-slate-700 bg-slate-900/30 p-4">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="rounded bg-slate-700 px-2 py-0.5 text-xs font-mono text-slate-200">
                        #{i + 1} · {KIND_LABEL[b.kind] || b.kind}
                      </span>
                      {!editable && (
                        <span className="rounded bg-amber-700/60 px-2 py-0.5 text-xs text-amber-100">
                          未知类型 · 仅查看
                        </span>
                      )}
                      {!b.enabled && (
                        <span className="rounded bg-slate-700 px-2 py-0.5 text-xs text-slate-300">已禁用</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => moveBlock(i, -1)} disabled={i === 0}
                        className="rounded border border-slate-600 px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-40">上移</button>
                      <button type="button" onClick={() => moveBlock(i, 1)} disabled={i === blocks.length - 1}
                        className="rounded border border-slate-600 px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-40">下移</button>
                      <button type="button" onClick={() => toggleBlock(i)}
                        className="rounded border border-slate-600 px-2 py-0.5 text-xs text-slate-200 hover:bg-slate-700">
                        {b.enabled ? '禁用' : '启用'}
                      </button>
                      <button type="button" onClick={() => removeBlock(i)}
                        className="rounded bg-rose-700/60 px-2 py-0.5 text-xs text-white hover:bg-rose-700">删除</button>
                    </div>
                  </div>

                  {b.kind === 'hero'         && <HeroEditor         block={b} onPatch={(p) => patchBlock(i, p)} />}
                  {b.kind === 'pricing'      && <PricingEditor      block={b} onPatch={(p) => patchBlock(i, p)} />}
                  {b.kind === 'features'     && <FeaturesEditor     block={b} onPatch={(p) => patchBlock(i, p)} />}
                  {b.kind === 'testimonials' && <TestimonialsEditor block={b} onPatch={(p) => patchBlock(i, p)} />}
                  {b.kind === 'cta'          && <CtaBlockEditor     block={b} onPatch={(p) => patchBlock(i, p)} />}
                  {b.kind === 'rich_text'    && <RichTextEditor     block={b} onPatch={(p) => patchBlock(i, p)} />}
                  {b.kind === 'media'        && <MediaEditor        block={b} onPatch={(p) => patchBlock(i, p)} />}
                  {b.kind === 'faq'          && <FaqEditor          block={b} onPatch={(p) => patchBlock(i, p)} />}
                  {!editable && <ReadonlyBlock block={b} />}
                </div>
              );
            })}
          </div>
        </section>

        {/* ---- Publish / rollback footer -------------------------------- */}
        <section className="rounded-lg border border-slate-700 bg-slate-800/40 p-5">
          <h2 className="mb-3 text-sm font-semibold text-slate-200">版本历史 & 回滚</h2>
          <p className="text-xs text-slate-400">
            发布会冻结当前草稿并创建新的 r{(revision ?? 0) + 1} 草稿继续编辑。回滚会把线上指向任意存在的历史 revision，不会影响草稿。
          </p>
          <button
            type="button"
            onClick={() => setRollback({ open: true, target: '', reason: '' })}
            disabled={page.published_revision === 0}
            className="mt-3 rounded border border-amber-700/70 px-3 py-1 text-xs text-amber-200 hover:bg-amber-900/30 disabled:opacity-40"
          >
            回滚到历史 revision…
          </button>
        </section>
      </main>

      {/* ---- Rollback dialog ---------------------------------------------- */}
      {rollback.open && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/80" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-lg border border-slate-700 bg-slate-900 p-5">
            <h3 className="text-sm font-semibold text-slate-100">回滚线上版本</h3>
            <p className="mt-1 text-xs text-slate-400">
              当前线上 r{page.published_revision} · 最新草稿 r{page.current_revision}
            </p>
            <label className="mt-3 block">
              <span className="text-sm font-medium text-slate-200">目标 revision</span>
              <input
                type="number"
                value={rollback.target}
                onChange={(e) => setRollback((x) => ({ ...x, target: e.target.value }))}
                min={1}
                max={page.current_revision}
                className="mt-1 w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:border-chango-500 focus:outline-none"
              />
            </label>
            <label className="mt-3 block">
              <span className="text-sm font-medium text-slate-200">回滚理由 (≥ 8 字)</span>
              <textarea
                rows={3}
                value={rollback.reason}
                onChange={(e) => setRollback((x) => ({ ...x, reason: e.target.value }))}
                className="mt-1 w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100 focus:border-chango-500 focus:outline-none"
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button"
                onClick={() => setRollback({ open: false, target: '', reason: '' })}
                className="rounded border border-slate-600 px-3 py-1 text-sm text-slate-200 hover:bg-slate-700">
                取消
              </button>
              <button type="button" onClick={doRollback}
                className="rounded bg-amber-600 px-3 py-1 text-sm font-semibold text-white hover:bg-amber-700">
                确认回滚
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
